/**
 * Auth Provider
 *
 * Main authentication provider that supports multiple auth backends via a registry.
 * Auth modules register their implementations using registerAuthProvider().
 *
 * Built-in support for OIDC (used by Zitadel, Keycloak, and generic OIDC providers).
 * Better Auth and other providers are registered by their respective modules.
 */

import { DefaultEntities, type OidcSettings } from "@atoms/atom-types";
import { WebStorageStateStore } from "oidc-client-ts";
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { DefaultNoAccessScreen } from "../components/DefaultNoAccessScreen";
import { DelayedRender, SPLASH_DELAY_MS } from "../components/DelayedRender";
import type {
  ErrorScreen,
  NoAccessScreen,
  NoAccessScreenProps,
} from "../FrontendProvision";
import { getBackendUrl } from "../getBackendUrl";
import { useHandleOidcEvents } from "../hooks/useHandleOidcEvents";
import { useUserStore } from "../hooks/useMyUserId";
import { makeGetAuthToken } from "../makeGetAuthToken";
import { formatUnknownError } from "../notifications/formatError";
import { SetIdentifier } from "../SetIdentifier";
import { useStartupSplashController } from "../StartupSplashController";
import {
  findAuthProvider,
  hasRegisteredProviders,
} from "./AuthProviderRegistry";
import { OidcUniversalAuthProvider } from "./useUniversalAuth";

type GetAuthTokenFn = () => Promise<string | null>;

const AuthTokenContext = createContext<GetAuthTokenFn | null>(null);

export const useAuthToken = () => {
  const context = useContext(AuthTokenContext);
  if (!context) {
    throw new Error("useAuthToken must be used within an AuthProvider");
  }
  return context;
};

/**
 * Type guard for OIDC settings (legacy format without type field)
 */
function isOidcSettings(data: unknown): data is OidcSettings {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  // OIDC settings have authority or metadataUrl, but no type field or type is not recognized
  return (
    !("type" in obj) ||
    obj.type === "oidc" ||
    (typeof obj.type === "string" && !findAuthProvider(data))
  );
}

interface AuthProviderProps {
  readonly children: ReactNode;
  readonly splashScreen: ReactNode;
  readonly errorScreen: ErrorScreen;
  readonly noAccessScreen?: NoAccessScreen;
}

export function AuthProvider({
  children,
  splashScreen,
  errorScreen,
  noAccessScreen,
}: Readonly<AuthProviderProps>) {
  const [authSettings, setAuthSettings] = useState<unknown>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const getAuthTokenRef = useRef<GetAuthTokenFn>(async () => null);
  const stableGetAuthToken = useCallback(
    (): Promise<string | null> => getAuthTokenRef.current(),
    [],
  );
  const [disableAuthFlows, setDisableAuthFlows] = useState(false);
  const { backendBaseUrl } = getBackendUrl();
  const startupSplash = useStartupSplashController();
  const completeStage = startupSplash?.completeStage;
  const finishStartup = startupSplash?.finishStartup;

  useEffect(() => {
    fetch(`${backendBaseUrl}/auth/oidc`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            "Die Einstellungen für die Authentifizierung konnten nicht abgerufen werden.",
          );
        }
        return response.json();
      })
      .then((data: unknown) => {
        // First, check if any registered provider can handle this
        const registeredProvider = findAuthProvider(data);

        if (registeredProvider) {
          // Use registered provider's token getter
          const tokenGetter = registeredProvider.createTokenGetter(
            data,
            backendBaseUrl,
          );
          getAuthTokenRef.current = tokenGetter;
          setAuthSettings(data);
          setIsLoading(false);
          completeStage?.("auth");
          finishStartup?.();
          return;
        }

        // Fall back to built-in OIDC handling
        if (isOidcSettings(data)) {
          const oidcData = data as OidcSettings & { bootstrap_user?: string };
          // Default redirect_uri if not provided
          oidcData.redirect_uri ??= globalThis.location.origin;
          // Ensure authority/metadataUrl defaults for dummy provider
          const backend = backendBaseUrl.replace(/\/$/, "");
          if (!oidcData.authority && !oidcData.metadataUrl) {
            oidcData.authority = `${backend}/auth`;
            oidcData.metadataUrl = `${backend}/auth/.well-known/openid-configuration`;
          } else if (oidcData.authority && !oidcData.metadataUrl) {
            const absAuthority = new URL(oidcData.authority, backend)
              .toString()
              .replace(/\/$/, "");
            oidcData.authority = absAuthority;
            oidcData.metadataUrl = `${absAuthority}/.well-known/openid-configuration`;
          } else if (!oidcData.authority && oidcData.metadataUrl) {
            const absMetadata = new URL(
              oidcData.metadataUrl,
              backend,
            ).toString();
            oidcData.metadataUrl = absMetadata;
            try {
              const u = new URL(absMetadata);
              const base = `${u.protocol}//${u.host}`;
              oidcData.authority = `${base}/auth`;
            } catch {
              // leave authority undefined if parsing fails
            }
          }
          oidcData.response_type ??= "code";
          oidcData.scope ??= "openid profile email";
          oidcData.loadUserInfo ??= false;
          oidcData.client_id ??= "dummy-client";
          if (
            oidcData.bootstrap_user &&
            oidcData.authority &&
            oidcData.client_id
          ) {
            try {
              const existing = localStorage.getItem(
                `oidc.user:${oidcData.authority}:${oidcData.client_id}`,
              );
              if (!existing) {
                localStorage.setItem(
                  `oidc.user:${oidcData.authority}:${oidcData.client_id}`,
                  oidcData.bootstrap_user,
                );
              }
            } catch (err) {
              console.warn(
                "Failed to bootstrap OIDC user in localStorage:",
                err,
              );
            }
          }

          let oidcGetAuthToken: GetAuthTokenFn;
          if (oidcData.authority) {
            oidcGetAuthToken = makeGetAuthToken(
              oidcData.authority ?? "",
              oidcData.client_id ?? "",
            );
          } else {
            // Fallback: find token by client_id across any authority
            oidcGetAuthToken = () => {
              const clientId = oidcData.client_id ?? "";
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key) continue;
                if (
                  key.startsWith("oidc.user:") &&
                  key.endsWith(`:${clientId}`)
                ) {
                  const raw = localStorage.getItem(key);
                  if (!raw) continue;
                  try {
                    const user = JSON.parse(raw) as { access_token?: string };
                    return Promise.resolve(user.access_token ?? null);
                  } catch {
                    // ignore parse errors
                  }
                }
              }
              return Promise.resolve(null);
            };
          }
          getAuthTokenRef.current = oidcGetAuthToken;
          setAuthSettings(oidcData);
          setDisableAuthFlows(Boolean(oidcData.bootstrap_user));
          setIsLoading(false);
          completeStage?.("auth");
          finishStartup?.();
          return;
        }

        // No matching provider found
        throw new Error(
          `Unknown auth type. ${hasRegisteredProviders() ? "No registered provider matched." : "No auth providers registered."} Data: ${JSON.stringify(data)}`,
        );
      })
      .catch((errUnknown) => {
        let errorToSet: Error;
        if (errUnknown instanceof Error) {
          errorToSet = errUnknown;
        } else {
          errorToSet = new Error(String(errUnknown));
        }

        if (errorToSet.message === "Failed to fetch") {
          errorToSet.message =
            "Leider ist der Server nicht erreichbar. Bitte versuchen Sie es später erneut. Code: ERR_OIDC_CONFIG";
        }

        setError(errorToSet);
        setIsLoading(false);
        completeStage?.("auth");
        finishStartup?.();
      });
  }, [backendBaseUrl, completeStage, finishStartup]);

  if (error) {
    return renderErrorScreen(errorScreen, error);
  }
  if (isLoading || authSettings === undefined) {
    return null;
  }

  // Check for registered provider first
  const registeredProvider = findAuthProvider(authSettings);
  if (registeredProvider) {
    const UniversalAuthWrapper =
      registeredProvider.createUniversalAuthWrapper();
    return (
      <AuthTokenContext.Provider value={stableGetAuthToken}>
        {registeredProvider.createProvider({
          children,
          splashScreen,
          errorScreen,
          noAccessScreen: noAccessScreen ?? DefaultNoAccessScreen,
          settings: authSettings,
          setGetAuthToken: (getter) => {
            getAuthTokenRef.current = getter;
          },
          UniversalAuthWrapper,
        })}
      </AuthTokenContext.Provider>
    );
  }

  // Fall back to built-in OIDC provider
  const oidcSettings = authSettings as OidcSettings & {
    bootstrap_user?: string;
  };
  return (
    <OidcAuthProvider
      {...oidcSettings}
      userStore={new WebStorageStateStore({ store: globalThis.localStorage })}
      onSigninCallback={() => {
        globalThis.history.replaceState(
          {},
          document.title,
          globalThis.location.pathname,
        );
      }}
      automaticSilentRenew={oidcSettings?.automaticSilentRenew ?? true}
    >
      <AuthTokenContext.Provider value={stableGetAuthToken}>
        <OidcUniversalAuthProvider>
          <AuthGate
            disableAuthFlows={disableAuthFlows}
            splashScreen={splashScreen}
            errorScreen={errorScreen}
            noAccessScreen={noAccessScreen ?? DefaultNoAccessScreen}
          >
            {children}
          </AuthGate>
        </OidcUniversalAuthProvider>
      </AuthTokenContext.Provider>
    </OidcAuthProvider>
  );
}

function AuthGate({
  children,
  disableAuthFlows,
  splashScreen,
  errorScreen,
  noAccessScreen,
}: Readonly<{
  children: ReactNode;
  disableAuthFlows: boolean;
  splashScreen: ReactNode;
  errorScreen: ErrorScreen;
  noAccessScreen: NoAccessScreen;
}>) {
  const { isAuthenticated, signoutRedirect } = useAuth();
  const userId = useUserStore((state) => state.userId);
  // If a bootstrap user was injected by settings, disable all auth flows
  const oidcUserResolution = useHandleOidcEvents({
    disableAuthFlows,
  });

  if (!isAuthenticated) {
    return null;
  }

  if (oidcUserResolution.status === "error" && oidcUserResolution.error) {
    return renderErrorScreen(errorScreen, oidcUserResolution.error);
  }

  if (
    oidcUserResolution.status === "no-access" &&
    oidcUserResolution.noAccessDetails
  ) {
    return renderNoAccessScreen(noAccessScreen, {
      ...oidcUserResolution.noAccessDetails,
      isAuthenticated: true,
      onRetry: () => globalThis.location.reload(),
      onSignOut: () => {
        void signoutRedirect({
          post_logout_redirect_uri: globalThis.location.origin,
        });
      },
    });
  }

  // Avoid mounting app context with the raw OIDC subject while the
  // backend-resolved Atom user id is still being loaded.
  if (oidcUserResolution.status !== "resolved" || !userId) {
    return <DelayedRender delayMs={SPLASH_DELAY_MS} fallback={splashScreen} />;
  }

  return (
    <SetIdentifier identifier={DefaultEntities.User} value={userId}>
      {children}
    </SetIdentifier>
  );
}

function renderErrorScreen(errorScreen: ErrorScreen, error: Error): ReactNode {
  if (typeof errorScreen === "function") {
    return errorScreen({ error });
  }
  if (React.isValidElement(errorScreen)) {
    return React.cloneElement(
      errorScreen as React.ReactElement<{ error?: Error }>,
      {
        error,
      },
    );
  }
  const formatted = formatUnknownError(error, {
    defaultMessage:
      "Die Authentifizierungseinstellungen konnten nicht geladen werden.",
    keyPrefix: "auth-provider",
  });
  return (
    <div>
      <p>{formatted.message}</p>
      {formatted.description ? <p>{formatted.description}</p> : null}
      <button type="button" onClick={() => globalThis.location.reload()}>
        Erneut versuchen
      </button>
    </div>
  );
}

function renderNoAccessScreen(
  noAccessScreen: NoAccessScreen,
  props: NoAccessScreenProps,
): ReactNode {
  if (typeof noAccessScreen === "function") {
    return noAccessScreen(props);
  }
  if (React.isValidElement(noAccessScreen)) {
    return React.cloneElement(
      noAccessScreen as React.ReactElement<Partial<NoAccessScreenProps>>,
      props,
    );
  }
  return <DefaultNoAccessScreen {...props} />;
}
